import Vue from 'vue'
import VueRouter from 'vue-router'
import homePageRoutes from './modules/homePage'
import { appointmentRoutes } from './modules/appointment'
import { parametersRoutes } from './parameters/index.js'
import { operateDashboardRoutes } from './modules/operate'
import { memberCenterRoutes } from './modules/memberCenter'
import { progressNotificationRoutes } from './modules/progressNotification'
import { marketingRoutes } from './modules/marketing'
import { batch } from '@/queue/bobAction'
import { sheetExportCenterRoutes } from './modules/sheetExportCenter'
import memberRoutes from './modules/member'
import store from '@/store'
import { get, includes } from 'lodash'
import { keepAliveConfig } from '@/config/keepAlive'
/* Layout */
import Layout from '@/layout/index.vue'
import dayjs from '@/lib/dayjs'

import baseRoutes from './modules/base'
import authRoutes from './modules/auth'
import testRoutes from './modules/test'

Vue.use(VueRouter)

/**
 * constantRoutes
 * 基本的頁面
 */
export const constantRoutes = [
  ...baseRoutes,
  ...authRoutes,
  ...testRoutes,
  {
    path: '/read-member-history-file/:shopId/:id/:downloadKey',
    name: 'Read Member History File',
    component: () => import('@/views/Members/ReadMemberHistoryFile'),
    hidden: true,
    props: true,
  },
]

/**
 * orgSettingsRoutes
 * 主要給集團使用的基本頁面
 * GIMMATEK 組織層只保留：分店管理、組織登入設定、分店設定、管理員設定、合約方案列表（CNCY-10766）
 */
export const orgSettingsRoutes = [
  {
    path: '/branch-shop-options',
    component: Layout,
    children: [
      {
        path: '',
        component: () => import('@/views/Basic/BranchShopOptions'),
        name: 'BranchShopOptions',
        meta: {
          title: 'page.branchManage.title',
          disabledQrcodeReader: true,
          bobPageAction: 'PAGE_VIEW_DASH_SHOP_OPTIONS',
          scopeType: 'OrgId',
        },
      },
    ],
  },
  {
    path: '/oauth-setting',
    component: Layout,
    children: [
      {
        path: '',
        component: () => import('@/views/Basic/OAuthSetting/OAuthSetting.vue'),
        name: 'OAuthSetting',
        meta: {
          title: '組織登入設定',
          action: 'admin.authServiceConfig.page',
          disabledQrcodeReader: true,
          bobPageAction: 'PAGE_VIEW_DASH_AUTH_SETTING',
          scopeType: 'OrgId',
        },
      },
      {
        path: 'edit/policy',
        hidden: true,
        component: () => import('@/views/Basic/OAuthSetting/EditPolicySetting/EditPolicySetting.vue'),
        name: 'EditPolicySetting',
        meta: {
          title: '組織登入設定',
          action: 'admin.authServiceConfig.page',
          disabledQrcodeReader: true,
          scopeType: 'OrgId',
        },
      },
    ],
  },

  {
    path: '/branch-shop-setting',
    component: Layout,
    children: [
      {
        path: '',
        name: 'BranchShopSetting',
        component: () => import('@/views/Basic/BranchShopSetting/BranchShopSetting.vue'),
        meta: {
          title: 'page.branchSetting.title',
          action: 'admin.shop.page',
          disabledQrcodeReader: true,
          bobPageAction: 'PAGE_VIEW_DASH_SHOP_LIST',
          scopeType: 'OrgId',
        },
      },
    ],
  },
  {
    path: '/admin-list',
    component: Layout,
    children: [
      {
        path: '',
        name: 'AdminList',
        component: () => import('@/views/AdminList/AdminList'),
        meta: {
          title: 'page.adminSetting.title',
          action: 'admin.user.page',
          disabledQrcodeReader: true,
          bobPageAction: 'PAGE_VIEW_DASH_ADMIN_LIST',
          scopeType: 'OrgId',
        },
      },
    ],
  },
  {
    path: '/contract-setting',
    component: Layout,
    children: [
      {
        path: '',
        component: () => import('@/views/Basic/ContractSetting'),
        name: 'ContractSetting',
        meta: {
          title: 'page.contractSetting.title',
          action: 'admin.contractPlan.page',
          disabledQrcodeReader: true,
          bobPageAction: 'PAGE_VIEW_DASH_CONTRACT_LIST',
          scopeType: 'OrgId',
        },
      },
    ],
  },
]

const basicRoutes = [
  // 基本模組：店家首頁（homePageRoutes）、LINE 通知紀錄查詢
  {
    path: '/basic',
    component: Layout,
    alwaysShow: true,
    isGroup: true,
    name: 'Basic',
    meta: {
      title: '基本模組',
    },
    children: [
      {
        path: '/line-push-message-log',
        component: () => import('@/views/Basic/LinePushMessageLog.vue'),
        name: 'LinePushMessageLog',
        meta: {
          title: 'LINE 通知紀錄查詢',
          action: 'admin.linePushMessageLog.page',
        },
      },
    ],
  },
  // 會員模組
  ...memberRoutes,
]

/**
 * 找不到對應路由時的 fallback，必須放在路由陣列最後
 * 任何動態註冊的路由集合都要包含它，否則未匹配的路徑會渲染空白頁 [CBGISSUE-264]
 */
export const notFoundRoute = { path: '*', redirect: '/branch-shop-options', hidden: true }

/**
 * asyncRoutes
 * 可以根據不同使用者顯示不同的頁面
 * GIMMATEK 店家層只保留：基本模組、會員、通知、行銷（僅分群推播）、預約、AI 服務人員績效、基本參數設定（CNCY-10766）
 * 下載中心保留給會員管理、預約列表的匯出檔下載
 */
export const asyncRoutes = () => {
  return [
    ...homePageRoutes,
    {
      path: '/daily-view',
      component: Layout,
      children: [
        {
          path: '',
          name: 'DailyView',
          component: () => import('@/views/DailyView/DailyView'),
          meta: {
            title: 'page.dailyView.title',
            action: 'adminView.appointmentCalendarByDay.page',
          },
        },
      ],
    },

    ...parametersRoutes,
    ...basicRoutes,
    ...progressNotificationRoutes,
    ...marketingRoutes,
    ...appointmentRoutes,
    ...memberCenterRoutes,
    ...operateDashboardRoutes,
    ...sheetExportCenterRoutes,
    notFoundRoute,
  ]
}

const createRouter = () =>
  new VueRouter({
    mode: 'history', // require service support
    scrollBehavior: () => ({ y: 0 }),
    routes: [...constantRoutes],
  })

const router = createRouter()

export function resetRouter () {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher // reset router
}

router.afterEach((to, from) => {
  console.log('🔥[debug] afterEach')
  if (to.meta.bobPageAction) {
    let scopeId = store.getters.shop
    if (to.meta.scopeType === 'OrgId') scopeId = store.getters.orgId
    const data = {
      scopeId,
      userId: store.getters.user?.id,
      date: dayjs().format('YYYY-MM-DD HH:mm'),
      action: to.meta.bobPageAction,
      path: to.path,
    }
    batch.create(data)
  }
  // const keepAlive = get(to, 'meta.keepAlive')
  // if (keepAlive) { store.commit('app/SET_KEEP_ALIVE', to.name) }
  if (includes(get(keepAliveConfig, `${from.name}.to`), to.name)) {
    store.commit('app/SET_KEEP_ALIVE', from.name)
  }
  // if (get(from, 'meta.group')) {
  //   console.log(get(from, 'meta.group'))
  //   store.commit('app/SET_KEEP_ALIVE', `${get(from, 'meta.group')}RouterViewWrapper`)
  // }
  if (
    !get(keepAliveConfig, to.name) &&
    !Object.values(keepAliveConfig).some((config) => includes(config.to, to.name))
  ) {
    store.dispatch('app/emptyKeepAlive')
  }

  store.commit('app/TOGGLE_PAGE_CHANGING', false)
})

// afterEach only fires when a navigation succeeds. If a beforeEach guard
// throws or its promise rejects, afterEach never runs and TOGGLE_PAGE_CHANGING
// stays true forever, leaving AppLoading stuck on screen. onError is the
// counterpart that fires on a failed navigation, so clear it here too.
router.onError(() => {
  store.commit('app/TOGGLE_PAGE_CHANGING', false)
})

export default router
